/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo, useRef, useState } from 'react';
import { Ionicons } from '@expo/vector-icons';
import { StyleSheet, Text, TextInput, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors'
import { typography } from '../../shared/theme/typography';
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketKpiStrip } from '../../shared/ticket/TicketKpiStrip'
import type { TicketKpiItem } from '../../shared/ticket/TicketKpiStrip'
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader'
import { TicketMoreActionsSheet } from '../../shared/ticket/TicketMoreActionsSheet'
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { CatalogStockProductSearchFields } from '../../shared/product-search/components/CatalogStockProductSearchFields';
import { searchProducts } from '../infra/productCatalogSearch';
import { sourceLabels } from '../screens/catalogScreen.constants';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import type { ProductSearchRow } from '../domain/catalogTypes';
import type { ProductLookupResult } from '../domain/barcodeLookup';
import { useOperationalFilterLayout } from '../../shared/filters/useOperationalFilterLayout';
import { useOperationalWebSearchFocus } from '../../shared/ui/hooks-screen/useOperationalWebSearchFocus';

type CatalogScreenHeaderProps = {
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  onOpenSearchScanner: () => void;
  onOpenCreateModal: () => void;
  /** CTA label for create action. Defaults to "Nuevo producto". */
  newItemLabel?: string;
  /** Label corto del CTA en la fila compacta mobile (default: newItemLabel). */
  newItemShortLabel?: string;
  /** KPI count label. Defaults to "Productos". */
  kpiItemsLabel?: string;
  /** Label del KPI de ítems visibles online (default: "En menú"). */
  kpiOnMenuLabel?: string;
  onOpenCategoriesManager: (section?: 'categories' | 'brands' | 'suppliers') => void;
  onOpenBulkPriceModal: () => void;
  onOpenCsvModal?: () => void;
  /** Export PLU para etiquetadora (weight_uom). */
  onOpenLabelScaleExport?: () => void;
  canEdit: boolean;
  /** Permiso manage_masters — Organizar / hub categorías·marcas·proveedores. */
  canManageMasters?: boolean;
  filterTrigger: React.ReactNode;
  totalProductCount: number | null;
  lookupFeedback: string | null;
  remoteSuggestion: Extract<ProductLookupResult, { kind: 'remote_hit' }> | null;
  getCategoryLabel: (slug?: string | null) => string;
  branchId?: string | null;
  onPickSearchSuggestion?: (row: ProductSearchRow) => void;
  pinnedCatalogProduct?: { label: string } | null;
  onClearPinnedCatalogSearch?: () => void;
  onUseRemoteSuggestion: () => void;
  onDismissRemoteSuggestion: () => void;
  showBarcodeCameraButton?: boolean;
  onSearchSubmitEditing?: () => void;
  /** Título del módulo en el PaperSheet (chrome MainTabs ya no lo muestra). */
  moduleTitle?: string;
  moduleMeta?: string;
  moduleKicker?: string;
  /** null = KPIs no cargados/fallaron → el tile muestra «—», no un 0 falso. */
  alertsOnPage?: number | null;
  incompleteOnPage?: number | null;
  /** Activos visibles en menú/tienda (global). */
  onMenuCount?: number | null;
  /** Servicios activos (global). */
  servicesCount?: number | null;
  /** Catálogo híbrido servicios → 2.º KPI = Servicios (si no hay filtro). */
  serviceCatalogEnabled?: boolean;
  /** Con búsqueda/filtros, el 2.º KPI pasa a «Resultados». */
  hasActiveFilters?: boolean;
  catalogTotalCount?: number | null;
  onToggleAlertFilter?: () => void;
  alertFilterActive?: boolean;
  onToggleIncompleteFilter?: () => void;
  incompleteFilterActive?: boolean;
  onToggleServicesKpi?: () => void;
  servicesKpiSelected?: boolean;
  /** Sin capability inventory: oculta KPI/filtro de alertas de stock. */
  showStockAlertKpi?: boolean;
};

export const CatalogScreenHeader = ({
  searchQuery,
  onSearchQueryChange,
  onOpenSearchScanner,
  onOpenCreateModal,
  newItemLabel = 'Nuevo producto',
  newItemShortLabel,
  kpiItemsLabel = 'Productos',
  kpiOnMenuLabel = 'En menú',
  onOpenCategoriesManager,
  onOpenBulkPriceModal,
  onOpenCsvModal,
  onOpenLabelScaleExport,
  canEdit,
  canManageMasters = false,
  filterTrigger,
  totalProductCount,
  lookupFeedback,
  remoteSuggestion,
  getCategoryLabel,
  branchId,
  onPickSearchSuggestion,
  pinnedCatalogProduct = null,
  onClearPinnedCatalogSearch,
  onUseRemoteSuggestion,
  onDismissRemoteSuggestion,
  showBarcodeCameraButton = true,
  onSearchSubmitEditing,
  moduleTitle = 'Tus productos',
  moduleMeta = 'Acá cargás y editás lo que vendés.',
  moduleKicker = 'Catálogo',
  alertsOnPage = null,
  incompleteOnPage = null,
  onMenuCount = null,
  servicesCount = null,
  serviceCatalogEnabled = false,
  catalogTotalCount = null,
  hasActiveFilters = false,
  onToggleAlertFilter,
  alertFilterActive = false,
  onToggleIncompleteFilter,
  incompleteFilterActive = false,
  onToggleServicesKpi,
  servicesKpiSelected = false,
  showStockAlertKpi = true,
}: CatalogScreenHeaderProps) => {
  const { layoutWide } = useOperationalFilterLayout();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  // Phone apaisado (poco alto, ancho ≥768 dispara layoutWide): la fila desk de
  // acciones queda superpuesta/rota — forzar el chrome compacto de una columna.
  const isPhoneLandscape = windowWidth > windowHeight && windowHeight < 500;
  const effectiveLayoutWide = layoutWide && !isPhoneLandscape;
  const searchInputRef = useRef<TextInput>(null);
  useOperationalWebSearchFocus(searchInputRef, {
    shortcutsEnabled: true,
  });
  const [moreActionsSheetOpen, setMoreActionsSheetOpen] = useState(false);

  const kpiProducts = totalProductCount != null ? String(totalProductCount) : '—';
  const kpiInList = catalogTotalCount != null ? String(catalogTotalCount) : '—';
  // Catálogo vacío sin filtros: el CTA vive en el empty state — no duplicar «Nuevo ítem».
  const isTrueEmpty = catalogTotalCount === 0 && !hasActiveFilters;
  const kpiOnMenu = onMenuCount != null ? String(onMenuCount) : '—';
  const kpiServices = servicesCount != null ? String(servicesCount) : '—';
  const kpiAlerts = alertsOnPage != null ? String(alertsOnPage) : '—';
  const kpiIncomplete = incompleteOnPage != null ? String(incompleteOnPage) : '—';
  const alertsCount = alertsOnPage ?? 0;
  const incompleteCount = incompleteOnPage ?? 0;

  const kpiItems = useMemo((): TicketKpiItem[] => {
    // Siempre 4 tiles. 2.º tile útil:
    // - con filtro/búsqueda → Resultados (lista)
    // - híbrido servicios → Servicios (tap filtra tipo)
    // - retail → En menú (visibles en tienda)
    const secondary: TicketKpiItem = hasActiveFilters
      ? {
          label: 'Resultados',
          value: kpiInList,
          tone: 'ok',
          accessibilityLabel: `Resultados del filtro: ${kpiInList}`,
          testID: 'catalog.kpi.results',
        }
      : serviceCatalogEnabled
        ? {
            label: 'Servicios',
            value: kpiServices,
            onPress: onToggleServicesKpi,
            selected: servicesKpiSelected,
            accessibilityLabel: 'Servicios — ver solo servicios',
            testID: 'catalog.kpi.services',
          }
        : {
            label: kpiOnMenuLabel,
            value: kpiOnMenu,
            accessibilityLabel: `${kpiOnMenuLabel}: ${kpiOnMenu}`,
            testID: 'catalog.kpi.on-menu',
          };

    const alertTile: TicketKpiItem = {
      label: 'Alertas',
      value: kpiAlerts,
      tone: alertsCount > 0 ? 'warn' : 'default',
      onPress: onToggleAlertFilter,
      selected: alertFilterActive,
      accessibilityLabel: 'Alertas — filtrar productos con alertas de stock',
      testID: 'catalog.kpi.alerts',
    };

    return [
      {
        label: kpiItemsLabel,
        value: kpiProducts,
        testID: 'catalog.kpi.total',
      },
      secondary,
      ...(showStockAlertKpi ? [alertTile] : []),
      {
        label: 'Incompletos',
        value: kpiIncomplete,
        tone: incompleteCount > 0 ? 'warn' : 'default',
        onPress: onToggleIncompleteFilter,
        selected: incompleteFilterActive,
        accessibilityLabel: 'Incompletos — filtrar productos incompletos',
        testID: 'catalog.kpi.incomplete',
      },
    ];
  }, [
    alertFilterActive,
    alertsCount,
    kpiAlerts,
    showStockAlertKpi,
    hasActiveFilters,
    incompleteFilterActive,
    incompleteCount,
    kpiIncomplete,
    kpiInList,
    kpiItemsLabel,
    kpiOnMenu,
    kpiOnMenuLabel,
    kpiProducts,
    kpiServices,
    onToggleAlertFilter,
    onToggleIncompleteFilter,
    onToggleServicesKpi,
    serviceCatalogEnabled,
    servicesKpiSelected,
  ]);

  // Mobile: las métricas 2×2 empujan la lista fuera del fold — solo se muestran
  // cuando aportan acción (Resultados con filtro, Alertas/Incompletos > 0).
  const showKpis = effectiveLayoutWide || hasActiveFilters || alertsCount > 0 || incompleteCount > 0;
  const kpiBlock = showKpis ? (
    <PaperSheet testID="catalog.kpi.sheet">
      <TicketKpiStrip
        items={kpiItems}
        layout={effectiveLayoutWide ? 'desk' : 'mobile'}
        variant="soft"
      />
    </PaperSheet>
  ) : null;

  // Patrón de Stock/Clientes: banner explícito cuando el rol es solo lectura.
  const readOnlyBanner = !canEdit ? (
    <View style={posUi.readOnlyBanner}>
      <Text style={posUi.readOnlyText}>Solo lectura. Mirá el catálogo, no lo editás.</Text>
    </View>
  ) : null;
  // ── Action sheet items (secondary actions) ──
  const sheetActions = useMemo(
    () =>
      [
        // Desktop ya tiene «Organizar» top-level — solo va en «Más» en mobile
        // (único acceso al hub de clasificación en layout compacto).
        effectiveLayoutWide
          ? null
          : {
              key: 'masters',
              label: 'Categorías, marcas y proveedores',
              hint: 'Cómo se agrupa lo que vendés',
              icon: 'grid-outline' as const,
              onPress: () => onOpenCategoriesManager('categories'),
              disabled: !canManageMasters,
            },
        {
          key: 'bulkPrice',
          label: 'Subir o bajar precios',
          hint: 'Un porcentaje para varios',
          icon: 'trending-up-outline' as const,
          onPress: onOpenBulkPriceModal,
          disabled: !canEdit,
        },
        onOpenCsvModal
          ? {
              key: 'csv',
              label: 'Importar o exportar CSV',
              hint: 'Cargá o bajá el catálogo',
              icon: 'document-text-outline' as const,
              onPress: onOpenCsvModal,
              disabled: false,
            }
          : null,
        onOpenLabelScaleExport
          ? {
              key: 'labelScale',
              label: 'Exportar a balanza',
              hint: 'PLU y precio para la etiquetadora',
              icon: 'scale-outline' as const,
              onPress: onOpenLabelScaleExport,
              disabled: !canEdit,
            }
          : null,
      ].filter((item): item is NonNullable<typeof item> => item != null),
    [
      canEdit,
      canManageMasters,
      effectiveLayoutWide,
      onOpenCategoriesManager,
      onOpenBulkPriceModal,
      onOpenCsvModal,
      onOpenLabelScaleExport,
    ]
  );

  const lookupBlocks = (
    <>
      {lookupFeedback ? (
        <View style={posUi.checkoutStatusBox}>
          <Text style={posUi.lookupFeedback}>{lookupFeedback}</Text>
        </View>
      ) : null}

      {remoteSuggestion ? (
        <View style={styles.lookupCard}>
          <View style={styles.lookupHeader}>
            <Text style={styles.lookupTitle}>Datos encontrados para este código</Text>
            <Text style={styles.lookupBadge}>{sourceLabels[remoteSuggestion.source]}</Text>
          </View>
          <Text style={styles.lookupName}>{remoteSuggestion.suggestion.name}</Text>
          <Text style={posUi.checkoutHint}>
            {remoteSuggestion.suggestion.brand ?? 'Sin marca'} ·{' '}
            {getCategoryLabel(remoteSuggestion.suggestion.categoryHint)}
          </Text>
          <Text style={posUi.checkoutHint}>
            Revisá el nombre, la marca y la categoría antes de cargar.
          </Text>
          <View style={styles.lookupActions}>
            <TouchableOpacity style={[posUi.primaryButton, styles.lookupActionBtn]} onPress={onUseRemoteSuggestion}>
              <Text style={posUi.primaryButtonText}>Cargar ítem</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[posUi.secondaryButton, styles.lookupActionBtn]} onPress={onDismissRemoteSuggestion}>
              <Text style={posUi.secondaryButtonText}>No usar</Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : null}
    </>
  );

  // ── Wide: proto = Nuevo arriba · tools = Buscar + Filtros + Más ──
  if (effectiveLayoutWide) {
    return (
      <View style={styles.headerStack}>
        {readOnlyBanner}
        <TicketModuleHeader
          kicker={moduleKicker}
          title={moduleTitle}
          meta={moduleMeta}
          testID="catalog.header.sheet"
          trailing={
            isTrueEmpty ? null : (
              <TouchableOpacity
                style={[styles.deskBtnGold, !canEdit && posUi.disabledCard]}
                onPress={onOpenCreateModal}
                disabled={!canEdit}
                accessibilityRole="button"
                accessibilityLabel={newItemLabel}
                testID="catalog.new-product"
              >
                <Text style={styles.deskBtnGoldText}>{newItemLabel}</Text>
              </TouchableOpacity>
            )
          }
          tools={
            <View style={styles.deskToolsRow}>
              <View style={styles.deskSearchFlex}>
                <CatalogStockProductSearchFields
                  inputRef={searchInputRef}
                  value={searchQuery}
                  onChangeText={onSearchQueryChange}
                  branchId={branchId ?? null}
                  searchProductsFn={searchProducts}
                  posExcludeMisc
                  onPickSuggestion={onPickSearchSuggestion}
                  pinnedProduct={pinnedCatalogProduct}
                  pinnedCaption="Filtrando el catálogo por"
                  onClearPinnedProduct={onClearPinnedCatalogSearch}
                  onPressScan={onOpenSearchScanner}
                  showBarcodeCameraButton={showBarcodeCameraButton}
                  placeholder="Buscar por nombre o código"
                  placeholderCompact="Buscar…"
                  compactTitleBreakpoint={400}
                  accessibilityLabel="Buscar"
                  onSubmitEditing={onSearchSubmitEditing}
                  autoFocusOnWeb={false}
                  scanTone="neutral"
                  showHidHint={false}
                  unifiedBoxStyle={styles.deskSearchBox}
                  inputStyle={styles.deskSearchInput}
                  sectionLabel={null}
                  textInputProps={{ testID: 'catalog.search.input' }}
                  // Sin matches, el único empty es el del listado (con acciones
                  // «Borrar filtros» / crear) — no duplicar un hint inline.
                  suggestionsEmptyMessage={null}
                />
              </View>
              {filterTrigger}
              {canManageMasters ? (
                <TouchableOpacity
                  style={styles.deskBtn}
                  onPress={() => onOpenCategoriesManager('categories')}
                  accessibilityRole="button"
                  accessibilityLabel="Organizar categorías, marcas y proveedores"
                  testID="catalog.organize.open"
                >
                  <Text style={styles.deskBtnText}>Organizar</Text>
                </TouchableOpacity>
              ) : null}
              {sheetActions.length > 0 ? (
                <TouchableOpacity
                  style={styles.deskBtn}
                  onPress={() => setMoreActionsSheetOpen(true)}
                  accessibilityRole="button"
                  accessibilityLabel="Más acciones"
                  testID="catalog.more.open"
                >
                  <Text style={styles.deskBtnText}>Más</Text>
                </TouchableOpacity>
              ) : null}
            </View>
          }
        >
          <TicketMoreActionsSheet
            visible={moreActionsSheetOpen}
            title="Más"
            actions={sheetActions}
            onClose={() => setMoreActionsSheetOpen(false)}
          />
        </TicketModuleHeader>
        {kpiBlock}
        {lookupBlocks}
      </View>
    );
  }

  // ── Mobile: proto = Buscar+Filtros · Nuevo full · Más para secundarias ──
  return (
    <View style={styles.headerStack}>
      {readOnlyBanner}
      <TicketModuleHeader
        kicker={moduleKicker}
        title={moduleTitle}
        meta={moduleMeta}
        testID="catalog.header.sheet"
        tools={
          <>
            <View style={styles.searchRowMobile}>
              <View style={styles.searchBoxMobile}>
                <Ionicons name="search" size={16} color={ticketColors.ink3} />
                <TextInput
                  ref={searchInputRef}
                  value={searchQuery}
                  onChangeText={onSearchQueryChange}
                  placeholder="Buscar"
                  placeholderTextColor={ticketColors.ink3}
                  style={styles.searchInputMobile}
                  accessibilityLabel="Buscar"
                  testID="catalog.search.input"
                  autoCorrect={false}
                  autoFocus={false}
                  returnKeyType={onSearchSubmitEditing ? 'search' : 'default'}
                  onSubmitEditing={onSearchSubmitEditing}
                  blurOnSubmit={false}
                />
                {searchQuery.trim().length > 0 ? (
                  <TouchableOpacity
                    onPress={() => onSearchQueryChange('')}
                    accessibilityRole="button"
                    accessibilityLabel="Limpiar búsqueda"
                  >
                    <Ionicons name="close-circle" size={18} color={ticketColors.ink2} />
                  </TouchableOpacity>
                ) : null}
              </View>
              {showBarcodeCameraButton && onOpenSearchScanner ? (
                <TouchableOpacity
                  style={styles.scanBtnMobile}
                  onPress={onOpenSearchScanner}
                  accessibilityRole="button"
                  accessibilityLabel="Escanear código de barras"
                >
                  <Ionicons name="barcode-outline" size={18} color={ticketColors.ink2} />
                </TouchableOpacity>
              ) : null}
            </View>
            {/* Row única de controles: Filtros + Más (incluye Organizar) + CTA.
                Antes eran 2 filas + CTA full-width (3 filas antes del primer ítem). */}
            <View style={styles.toolsRowMobile}>
              <View style={styles.toolFlexMobile}>{filterTrigger}</View>
              {sheetActions.length > 0 ? (
                <TouchableOpacity
                  style={[styles.actionMoreMobile, styles.toolFlexMobile]}
                  onPress={() => setMoreActionsSheetOpen(true)}
                  activeOpacity={0.6}
                  accessibilityRole="button"
                  accessibilityLabel="Más acciones"
                  testID="catalog.more.open"
                >
                  <Text style={styles.moreBtnText}>Más</Text>
                </TouchableOpacity>
              ) : null}
              {!isTrueEmpty ? (
                <TouchableOpacity
                  style={[styles.actionPrimaryRowMobile, !canEdit && posUi.disabledCard]}
                  onPress={onOpenCreateModal}
                  disabled={!canEdit}
                  activeOpacity={0.7}
                  accessibilityRole="button"
                  accessibilityLabel={newItemLabel}
                  accessibilityState={{ disabled: !canEdit }}
                  testID="catalog.new-product"
                >
                  <Text style={styles.actionPrimaryTextMobile} numberOfLines={1}>
                    {newItemShortLabel ?? newItemLabel}
                  </Text>
                </TouchableOpacity>
              ) : null}
            </View>

            {pinnedCatalogProduct?.label && onClearPinnedCatalogSearch ? (
              <TouchableOpacity
                style={styles.pinnedChipMobile}
                onPress={onClearPinnedCatalogSearch}
                activeOpacity={0.6}
                accessibilityRole="button"
                accessibilityLabel="Quitar filtro de producto"
              >
                <Text style={styles.pinnedChipTextMobile} numberOfLines={1}>
                  {pinnedCatalogProduct.label}
                </Text>
                <Ionicons name="close-circle" size={16} color={ticketColors.ink3} />
              </TouchableOpacity>
            ) : null}
          </>
        }
      />

      {kpiBlock}

      <TicketMoreActionsSheet
        visible={moreActionsSheetOpen}
        title="Más"
        actions={sheetActions}
        onClose={() => setMoreActionsSheetOpen(false)}
      />

      {lookupFeedback ? (
        <View style={posUi.checkoutStatusBox}>
          <Text style={posUi.lookupFeedback}>{lookupFeedback}</Text>
        </View>
      ) : null}

      {remoteSuggestion ? (
        <View style={styles.lookupCard}>
          <View style={styles.lookupHeader}>
            <Text style={styles.lookupTitle}>Datos encontrados para este código</Text>
            <Text style={styles.lookupBadge}>{sourceLabels[remoteSuggestion.source]}</Text>
          </View>
          <Text style={styles.lookupName}>{remoteSuggestion.suggestion.name}</Text>
          <Text style={posUi.checkoutHint}>
            {remoteSuggestion.suggestion.brand ?? 'Sin marca'} ·{' '}
            {getCategoryLabel(remoteSuggestion.suggestion.categoryHint)}
          </Text>
          <Text style={posUi.checkoutHint}>
            Revisá el nombre, la marca y la categoría antes de cargar.
          </Text>
          <View style={styles.lookupActions}>
            <TouchableOpacity style={[posUi.primaryButton, styles.lookupActionBtn]} onPress={onUseRemoteSuggestion}>
              <Text style={posUi.primaryButtonText}>Cargar ítem</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[posUi.secondaryButton, styles.lookupActionBtn]} onPress={onDismissRemoteSuggestion}>
              <Text style={posUi.secondaryButtonText}>No usar</Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  headerStack: {
    gap: 10,
    paddingBottom: spacing.sm,
  },
  deskToolsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 8,
  },
  deskSearchFlex: {
    flex: 1,
    minWidth: 180,
  },
  deskSearchBox: {
    backgroundColor: ticketColors.tile,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    height: 42,
    minHeight: 42,
    paddingVertical: 0,
    paddingHorizontal: 12,
    gap: 0,
  },
  deskSearchInput: {
    fontSize: 14,
    fontWeight: "500",
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink,
    height: 42,
    paddingVertical: 0,
  },
  deskBtn: {
    height: 42,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
    alignItems: "center",
    justifyContent: "center",
  },
  deskBtnText: {
    fontSize: 13,
    fontWeight: "700",
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  deskBtnGold: {
    height: 42,
    paddingHorizontal: 14,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
    alignItems: "center",
    justifyContent: "center",
  },
  deskBtnGoldText: {
    fontSize: 13,
    fontWeight: "700",
    color: ticketColors.goldInk,
    fontFamily: ticketFonts.sans,
  },
  lookupCard: {
    ...ticketSurfaces.paperSheet,
    ...ticketSurfaces.paperSheetPadded,
    gap: spacing.xs,
  },
  lookupHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: spacing.sm,
  },
  lookupTitle: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: "700",
  },
  lookupBadge: {
    ...typography.caption,
    color: ticketColors.ink2,
    backgroundColor: ticketColors.paper2,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    fontWeight: "700",
  },
  lookupName: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: "700",
  },
  lookupActions: {
    flexDirection: "row",
    gap: spacing.sm,
    flexWrap: "wrap",
    marginTop: spacing.sm,
  },
  lookupActionBtn: {
    flex: 1,
    minWidth: 120,
  },
  searchRowMobile: {
    flexDirection: "row",
    gap: 8,
    marginBottom: 0,
  },
  toolsRowMobile: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  toolFlexMobile: {
    flex: 1,
  },
  searchBoxMobile: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 12,
    height: 42,
    borderRadius: 10,
    backgroundColor: ticketColors.tile,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  searchInputMobile: {
    flex: 1,
    fontSize: 14,
    fontWeight: "500",
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink,
    padding: 0,
  },
  scanBtnMobile: {
    width: 42,
    height: 42,
    borderRadius: 10,
    backgroundColor: ticketColors.paper2,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    alignItems: "center",
    justifyContent: "center",
  },
  actionPrimaryRowMobile: {
    flexGrow: 1.4,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    height: 42,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 10,
  },
  actionPrimaryTextMobile: {
    fontSize: 14,
    fontWeight: "800",
    color: ticketColors.goldInk,
    fontFamily: ticketFonts.sans,
  },
  actionMoreMobile: {
    height: 42,
    minWidth: 52,
    paddingHorizontal: 14,
    borderRadius: 10,
    backgroundColor: ticketColors.tile,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    alignItems: "center",
    justifyContent: "center",
  },
  moreBtnText: {
    fontSize: 13,
    fontWeight: "700",
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  pinnedChipMobile: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: ticketColors.paper,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    alignSelf: "flex-start",
  },
  pinnedChipTextMobile: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: "600",
  },
});